import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from 'react-phone-number-input';
import VerificationInput from 'react-verification-input';
import { NewPhoneVerificationSchema } from '../../schemas';
import { useAppDispatch, useAppSelector } from '../../../../store';
import {
  editPhone,
  patientState,
  sendVerificationCode,
  verifyPhoneCode,
} from '../../api/AccountSlice';
import { getMeThunk, user } from '../../../account/api/accountSlice';
import alert from '../../../../utils/alert';
import CustomModal from '../../../app/components/CustomModal';
import ResendCodeButton from '../../../../components/common/ResendCodeButton';
import phoneToSendVerificationCodeParams from '../../../../utils/phoneToSendVerificationCodeParams';
import phoneToParams from '../../../../utils/phoneToParams';
import formatNumberHandler from '../../../../utils/formatNumberHandler';
import { searchUserThunk } from '../../../register/api/registerSlice';

type PhoneFormData = {
  phone: string;
};

const NewPhoneVerification = ({
  phoneEditable,
  setPhoneEditable,
}: {
  phoneEditable: boolean;
  setPhoneEditable: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const phoneCountryCode = '+1';
  const { isLoading } = useAppSelector(patientState);
  const [step, setStep] = useState<number>(0);
  const [verCode, setVerCode] = useState<string>('');
  const [verCodeResUUID, setVerCodeResUUID] = useState<string>('');
  const [isResending, setIsResending] = useState<boolean>(false);
  const [phoneChanged, setPhoneChanged] = useState<boolean>(true);
  const [checkingPhoneAvailability, setCheckingPhoneAvailability] = useState<boolean>(false);
  const [phoneAvailable, setPhoneAvailable] = useState<boolean>(true);
  const userInfo = useAppSelector(user);

  const [submitPhoneLoader, setSubmitPhoneLoader] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
  } = useForm<PhoneFormData>({
    mode: 'onChange',
    resolver: yupResolver(NewPhoneVerificationSchema),
  });

  useEffect(() => {
    if (phoneChanged) {
      setPhoneChanged(false);
    }
  }, [phoneChanged]);

  const onSubmit = async (values: any) => {
    if (step === 0) {
      if (!isValidPhoneNumber(phoneCountryCode + values.phone)) {
        alert.error(t('Please enter a valid phone number.'));
        return;
      }

      const params = phoneToSendVerificationCodeParams(values.phone, phoneCountryCode);
      try {
        const data = await dispatch(sendVerificationCode(params)).unwrap();
        setVerCodeResUUID(data.resource.uuid);
        setStep(step + 1);
      } catch (e) {
        alert.error(t('Something went wrong. Please try again.'));
      }
    } else if (step === 1) {
      try {
        setSubmitPhoneLoader(true);
        const data = await dispatch(verifyPhoneCode({ verCodeResUUID, verCode })).unwrap();
        if (!data.resource.verified) {
          alert.error(t('Code is incorrect. Please enter a valid code.'));
        } else {
          const params = phoneToParams(userInfo, values.phone, phoneCountryCode, 'US');
          await dispatch(editPhone(params)).unwrap();

          await dispatch(getMeThunk()).unwrap(); // refetech updated telecom
          alert.success(t('Your phone number has been updated successfully.'));
          setPhoneEditable(false);
        }
      } catch (e) {
        alert.error(t('Something went wrong. Please try again.'));
      } finally {
        setSubmitPhoneLoader(false);
      }
    }
  };

  const onResendButtonSubmit = async () => {
    setIsResending(true);
    const params = phoneToSendVerificationCodeParams(getValues('phone'), phoneCountryCode);
    try {
      const data = await dispatch(sendVerificationCode(params)).unwrap();
      setVerCodeResUUID(data.resource.uuid);
      alert.success(t('Verification code send successfully!'));
    } catch (e) {
      alert.error(t('Something went wrong. Please try again.'));
    } finally {
      setIsResending(false);
      setVerCode('');
    }
  };

  const title = () => {
    switch (step) {
      case 0:
        return t('New phone verification');
      case 1:
        return t('Check your phone');
      default:
        return '';
    }
  };

  const helpText = () => {
    switch (step) {
      case 0:
        return t('Enter the new phone number where you will receive a verification code.');
      case 1:
        return (
          <>
            <p>
              {t('We sent a code to ')}
              <strong>{formatNumberHandler(phoneCountryCode + getValues('phone'))}</strong>
            </p>
            <p>{t('Enter the code you received via SMS.')}</p>
          </>
        );
      default:
        return '';
    }
  };

  const confirmText = () => {
    switch (step) {
      case 0:
        return t('Send Verification Code');
      case 1:
        return t('Confirm');
      default:
        return '';
    }
  };

  const checkIfEmailOrPhoneAvailable = useCallback(
    (searchResult: any, value: string, type: string) => {
      if (searchResult.entry && searchResult.entry.length > 0) {
        const item = searchResult.entry[0]?.resource?.telecom?.find(
          (item: { system: string; value: string }) => item.system === type,
        );
        setPhoneAvailable(!(item && item.value.toLowerCase() === value.toLowerCase()));
      } else {
        setPhoneAvailable(true);
      }
      setCheckingPhoneAvailability(false);
    },
    [],
  );

  const searchPhoneNumber = useCallback(
    async (value: string, type: string) => {
      setCheckingPhoneAvailability(true);
      try {
        const searchBody = {
          telecom: value,
          limit: 1,
        };

        const resultAction = await dispatch(searchUserThunk(searchBody));

        if (searchUserThunk.fulfilled.match(resultAction)) {
          checkIfEmailOrPhoneAvailable(resultAction.payload, value, type);
        } else {
          setPhoneAvailable(true);
        }
      } catch (error) {
        setPhoneAvailable(true);
      } finally {
        setCheckingPhoneAvailability(false);
      }
    },
    [dispatch, checkIfEmailOrPhoneAvailable],
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const phoneNumber = e.target.value;
      setValue('phone', phoneNumber, { shouldValidate: true });
      if (phoneNumber.length >= 10) {
        searchPhoneNumber(phoneNumber, 'phone');
      } else {
        setPhoneAvailable(true);
      }
      setPhoneChanged(true);
    },
    [setValue, searchPhoneNumber],
  );

  return (
    <CustomModal
      show={phoneEditable}
      title={title()}
      helpText={helpText()}
      confirmText={confirmText()}
      cancelText='Cancel'
      useFlexibleFooter={true}
      handleCancel={() => {
        setPhoneEditable(false);
      }}
      handleConfirm={handleSubmit(onSubmit)}
      editPersonalDetails={
        step === 0 ? !isValid || !phoneAvailable || checkingPhoneAvailability : verCode.length !== 6
      }
      isSubmitting={isLoading || submitPhoneLoader}
    >
      <div className='mt-3'>
        {step === 0 && (
          <Form>
            <Form.Group>
              <Form.Label>{t('New Phone')}</Form.Label>
              <Form.Control
                type='text'
                {...register('phone', {
                  onChange: handlePhoneChange,
                })}
              />
              {errors.phone?.message && (
                <Form.Text className='text-danger'>{t(errors.phone.message)}</Form.Text>
              )}
              {!checkingPhoneAvailability && !phoneAvailable && (
                <Form.Text className='text-danger'>
                  {t('This Phone Number is already in use.')}
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        )}
        {step === 1 && (
          <>
            <VerificationInput
              value={verCode}
              length={6}
              placeholder=''
              onChange={setVerCode}
              classNames={{
                character: 'otp-input',
                characterSelected: 'otp-input--selected',
                container: 'ps-1 pe-1 mb-4 w-100',
              }}
              validChars='0-9'
              inputProps={{ inputMode: 'numeric' }}
            />
            <ResendCodeButton
              onClick={onResendButtonSubmit}
              label={t('Resend Code')}
              seconds={30}
              isLoading={isResending}
              minimal={true}
            />
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default NewPhoneVerification;
